import { useEffect, useState } from "react";
import { Banknote, CheckCircle2, Clock, Plus, Trash2, Search, Landmark, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fetchBlob, saveBlob } from "@/lib/api";
import { todayISO, fmtDate } from "@/lib/util";
import { Money, Loader, Field } from "@/components/common";
import { PageTitle } from "@/pages/admin/ui";
import Modal from "@/components/Modal";
import DateInput from "@/components/DateInput";

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
export const monthName = (m) => (m ? `${AR_MONTHS[Number(m.slice(5)) - 1]} ${m.slice(0, 4)}` : "");
const TypeTag = ({ p }) => <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${p.type === "staff" ? "bg-purple-50 text-purple-700" : "bg-blue-50 text-blue-700"}`}>{p.type === "staff" ? "موظف إدارة" : p.ref}</span>;

const Expected = ({ p, onEdit }) => (
  <button data-testid={`salary-expected-${p.id}`} onClick={() => onEdit(p)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand">
    <Landmark className="w-3.5 h-3.5" />{p.expected_fils == null ? "حدّد المبلغ المودع" : <>المودع: <Money fils={p.expected_fils} /></>}
  </button>
);

function ExpectedModal({ person, onClose, onSaved }) {
  const [v, setV] = useState("");
  useEffect(() => { setV(person?.expected_fils != null ? (person.expected_fils / 1000).toFixed(3) : ""); }, [person]);
  const save = async () => {
    try { await api.put("/salaries/expected", { person_type: person.type, person_id: person.id, amount: v }); toast.success("تم الحفظ"); onClose(); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal open={!!person} onClose={onClose} title="المبلغ المودع في البنك" testid="expected-modal">
      {person && <div className="space-y-3">
        <div className="rounded-xl bg-slate-50 p-3"><b>{person.name}</b></div>
        <Field label="المبلغ الشهري الذي تودعه الشركة (د.ك)" hint="اتركه فارغاً لإلغائه"><input data-testid="expected-amount" className="field ltr text-left text-xl font-black" inputMode="decimal" placeholder="0.000" value={v} onChange={(e) => setV(e.target.value)} /></Field>
        <button data-testid="expected-save" onClick={save} className="btn btn-lg btn-blue w-full">حفظ</button>
      </div>}
    </Modal>
  );
}

function PrintBtn({ month }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { saveBlob(await fetchBlob(`/salaries/pdf?month=${month}`), `salaries-${month}.pdf`); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return <button data-testid="salary-print-btn" onClick={go} disabled={busy} className="btn btn-md btn-dark">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}طباعة كشف الشهر PDF</button>;
}

function AddModal({ person, month, onClose, onSaved }) {
  const [f, setF] = useState({ amount: "", date: todayISO(), note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ amount: "", date: todayISO(), note: "" }); }, [person]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/salaries", { person_type: person.type, person_id: person.id, month, ...f });
      toast.success("تم تسجيل التسليم");
      onClose();
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Modal open={!!person} onClose={onClose} title="تسجيل تسليم راتب" testid="salary-modal">
      {person && <div className="space-y-3">
        <div className="rounded-xl bg-slate-50 p-3"><b>{person.name}</b> — {monthName(month)}</div>
        <Field label="المبلغ الذي سلّمه (د.ك)" required><input data-testid="salary-amount" className="field ltr text-left text-xl font-black" inputMode="decimal" placeholder="0.000" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
        <Field label="تاريخ التسليم"><DateInput testid="salary-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="ملاحظة (اختياري)"><input data-testid="salary-note" className="field" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <button data-testid="salary-save" disabled={busy || !f.amount} onClick={save} className="btn btn-lg btn-blue w-full">حفظ</button>
      </div>}
    </Modal>
  );
}

function Handed({ list, onDelete, onAdd, onEdit }) {
  if (!list.length) return <p className="text-slate-500 text-sm py-6 text-center">لا يوجد تسليم مسجّل لهذا الشهر</p>;
  return <div className="divide-y">{list.map((p) => (
    <div key={`${p.type}-${p.id}`} className="py-3" data-testid={`handed-${p.id}`}>
      <div className="flex items-center justify-between gap-3"><span className="min-w-0"><b className="block truncate">{p.name}</b><span className="flex flex-wrap items-center gap-2"><TypeTag p={p} /><Expected p={p} onEdit={onEdit} /></span>
        {p.diff_fils > 0 && <span className="block text-xs font-bold text-red-600 mt-0.5" data-testid={`salary-diff-${p.id}`}>سلّم أقل بـ <Money fils={p.diff_fils} /></span>}
        {p.diff_fils < 0 && <span className="block text-xs font-bold text-amber-600 mt-0.5" data-testid={`salary-diff-${p.id}`}>سلّم أكثر بـ <Money fils={-p.diff_fils} /></span>}
        {p.diff_fils === 0 && <span className="block text-xs font-bold text-green-700 mt-0.5" data-testid={`salary-diff-${p.id}`}>سلّم المبلغ كاملاً</span>}</span><span className="flex items-center gap-2 shrink-0"><Money fils={p.amount_fils} className="text-green-700 text-lg" />
        <button data-testid={`salary-addmore-${p.id}`} onClick={() => onAdd(p)} className="btn btn-sm btn-ghost" title="إضافة تسليم آخر"><Plus className="w-4 h-4" /></button></span></div>
      {p.records.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-xs text-slate-500 mt-1">
          <span>{fmtDate(r.date)} • <Money fils={r.amount_fils} /> {r.note && `• ${r.note}`} • بواسطة {r.created_by}</span>
          <button data-testid={`salary-del-${r.id}`} onClick={() => onDelete(r)} className="text-red-600 p-1" aria-label="حذف"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  ))}</div>;
}

function Pending({ list, onAdd, onEdit }) {
  if (!list.length) return <p className="text-green-700 font-bold text-sm py-6 text-center">الجميع سلّموا لهذا الشهر</p>;
  return <div className="divide-y">{list.map((p) => (
    <div key={`${p.type}-${p.id}`} className="py-3 flex items-center justify-between gap-3" data-testid={`pending-${p.id}`}>
      <span className="min-w-0"><b className="block truncate">{p.name}</b><span className="flex flex-wrap items-center gap-2"><TypeTag p={p} /><Expected p={p} onEdit={onEdit} /></span></span>
      <button data-testid={`salary-add-${p.id}`} onClick={() => onAdd(p)} className="btn btn-sm btn-blue shrink-0"><Plus className="w-4 h-4" />تسجيل تسليم</button>
    </div>
  ))}</div>;
}

const Col = ({ title, icon: I, tone, count, children, testid }) => (
  <div className="card p-5" data-testid={testid}>
    <h3 className={`text-lg font-extrabold flex items-center gap-2 mb-2 ${tone}`}><I className="w-6 h-6" />{title}<span className="text-sm bg-slate-100 text-slate-600 rounded-full px-2">{count}</span></h3>
    {children}
  </div>
);

export default function Salaries() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [d, setD] = useState(null);
  const [months, setMonths] = useState([]);
  const [add, setAdd] = useState(null);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const load = () => {
    api.get(`/salaries?month=${month}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/salaries/months").then((r) => setMonths(r.data)).catch(() => {});
  };
  useEffect(() => { setD(null); load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (r) => {
    if (!window.confirm(`حذف تسليم ${r.person_name}؟`)) return;
    try { await api.delete(`/salaries/${r.id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const flt = (xs) => xs.filter((p) => !q || p.name.includes(q) || (p.ref || "").includes(q) || (p.phone || "").includes(q));
  const cy = Number(todayISO().slice(0, 4));
  return (
    <div>
      <PageTitle title="نظام متابعة الرواتب" sub="من سلّم مبلغ الراتب للشركة ومن لم يسلّم" icon={Banknote} color="text-green-700" />
      <div className="card p-4 mb-5 flex flex-wrap items-center gap-3">
        <select data-testid="salary-month" className="field field-sm w-32" value={month.slice(5)} onChange={(e) => setMonth(`${month.slice(0, 4)}-${e.target.value}`)}>
          {AR_MONTHS.map((n, i) => <option key={n} value={String(i + 1).padStart(2, "0")}>{n}</option>)}</select>
        <select data-testid="salary-year" className="field field-sm w-28" value={month.slice(0, 4)} onChange={(e) => setMonth(`${e.target.value}-${month.slice(5)}`)}>
          {[cy + 1, cy, cy - 1, cy - 2, cy - 3].map((y) => <option key={y} value={y}>{y}</option>)}</select>
        <label className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-slate-400" />
          <input data-testid="salary-search" className="field field-sm ps-9 w-full" placeholder="بحث بالاسم أو الرقم أو الهاتف" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        {d && <div className="ms-auto text-end" data-testid="salary-total"><div className="text-xs text-slate-500">إجمالي المستلم — {monthName(month)}</div><Money fils={d.total_fils} className="text-2xl text-green-700" /></div>}
        <PrintBtn month={month} />
      </div>
      {d && d.expected_total_fils > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="salary-summary">
          {[["إجمالي المودع في البنك", d.expected_total_fils, "text-slate-900", "expected"], ["إجمالي المستلم", d.total_fils, "text-green-700", "received"], ["الناقص (لم يُسلَّم)", d.shortfall_fils, "text-red-600", "shortfall"]].map(([l, v, c, k]) => (
            <div key={k} className="card p-4"><div className="text-sm text-slate-500">{l}</div><Money fils={v} className={`text-2xl ${c}`} testid={`salary-sum-${k}`} /></div>
          ))}
        </div>
      )}
      {!d ? <Loader /> : (
        <div className="grid lg:grid-cols-2 gap-5">
          <Col title="سلّموا الراتب" icon={CheckCircle2} tone="text-green-700" count={flt(d.handed).length} testid="salary-handed"><Handed list={flt(d.handed)} onDelete={del} onAdd={setAdd} onEdit={setEdit} /></Col>
          <Col title="لم يسلّموا بعد" icon={Clock} tone="text-red-600" count={flt(d.pending).length} testid="salary-pending"><Pending list={flt(d.pending)} onAdd={setAdd} onEdit={setEdit} /></Col>
        </div>
      )}
      {months.length > 0 && (
        <div className="card p-5 mt-5" data-testid="salary-months">
          <h3 className="text-lg font-extrabold mb-3">إجمالي المستلم لكل شهر</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {months.map((m) => (
              <button key={m.month} data-testid={`salary-month-${m.month}`} onClick={() => setMonth(m.month)}
                className={`rounded-xl border p-3 text-start transition-colors ${m.month === month ? "border-green-600 bg-green-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <div className="text-sm text-slate-600">{monthName(m.month)}</div><Money fils={m.total_fils} className="text-green-700" /><div className="text-xs text-slate-400">{m.count} تسليم</div>
              </button>
            ))}
          </div>
        </div>
      )}
      <AddModal person={add} month={month} onClose={() => setAdd(null)} onSaved={load} />
      <ExpectedModal person={edit} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
